/* eslint-disable
  no-param-reassign,
  no-underscore-dangle,
  no-console,
  class-methods-use-this,
*/
import './coa.d';
import { validate } from 'uuid';
import {
  get,
  post,
  put,
  del,
} from '../http';
import {
  getAmpersendParams,
  getDestinationURLParam,
  getAllAmpersendParams,
} from '../mymove';
import { isDesktop } from '../utils/deviceType';

const coaUUIDFromURL = new URL(window.location)?.pathname?.split('/')?.pop();
const parsedUUID = validate(coaUUIDFromURL) ? coaUUIDFromURL : '';

const deviceType = isDesktop() ? 'DESKTOP' : 'MOBILE';

/**
 * An API layer used to kick off MGO API requests
 */
export default class COA {
  /**
   * @param {String} api - the fully qualified domain to issue requests against
   * @param {String} origin - used for coa creation and source identity for EMF
   * @param {String} postbackURL
   * @param {String} coaUUID - The uuid
   */
  constructor({
    api = null,
    origin = 'MGO',
    postbackURL = '',
    coaUUID = '',
    sid = '',
    orderUuid = '',
  }) {
    /**
     * The fully qualified base url for the MGO API
     * @type {String}
     */
    this.api = api;

    /**
     * Used during the Global Payment handoff to redirect on success
     * @type {String}
     */
    this.postbackURL = postbackURL;

    /**
     * Used to identify the application communicating with the API
     * @type {String}
     */
    this.origin = origin;

    /**
     * The current COA in progress
     * @type {String}
     */
    this.coaUUID = coaUUID;

    /**
     * The current COA in progress order UUID. For the primary business this matches coaUUID.
     * @type {String}
     */
    this.orderUuid = orderUuid;
    /**
     * The Visit Id of the current COA
     * Hydrated on Get and Create
     * @type {String}
     */
    this.visitId = '';

    /**
     * sid is the session id used to join many coas to one user
     * @type {String}
     */
    this.sid = sid;

    /**
     * deviceType is the type of device a user is using to create a coa
     * @type {String}
     */
    this.deviceType = deviceType;
  }

  reset() {
    this.coaUUID = '';
    this.orderUuid = '';
    this.visitId = '';
  }

  /**
   * Retrieve Application Configuration Details
   * These config items relate to Command Center
   * @method configGet
   * @return {Promise<Config>}
   */
  async configGet() {
    return get(`${this.api}/mgo-api/api/v1/app/config`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Retreive updated ICOA amount based on COA data
   * @return {Promise<String>}
   */
  async icoaAmountGet() {
    return get(`${this.api}/mgo-api/api/v1/payment/${this.coaUUID}`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Verify google recaptcha v3
   * https://developers.google.com/recaptcha/docs/v3#programmatically_invoke_the_challenge
   * @param {String} responseToken - token from recaptcha V3
   * @param {String} action - the recaptcha action
   * @return {Promise}
   */
  async recaptchaVerify(responseToken, action) {
    return post(`${this.api}/mgo-api/api/v1/rc`, {
      orderUuid: this.coaUUID,
      action,
      responseToken,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Get Global Payment forwarding Information
   * @param {String} returnUrl - The url to return a user to if they use GP Back
   * @param {String} suffixUrl - This is a string that is attached to the end of the url
   * @return {Promise<GlobalPaymentResponse>}
   */
  async globalPaymentURLGet(returnUrl, suffixUrl = '') {
    return post(`${this.api}/mgo-api/api/v1/payment`, {
      orderUuid: this.coaUUID,
      postbackUrl: `${this.postbackURL}?coa_uuid=${this.coaUUID}${suffixUrl}`,
      returnUrl,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Determine if the COA is DCOA or not
   * @return {Promise<DCOA>}
   */
  async isDCOA() {
    return get(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Check COA to see if there's exceptions
   * Used on ICOA flow - MoveCombined/Disclaimer, VerifyInfoEdit
   * @return {Promise}
   */
  async coaCheckByUUID(coaUUID) {
    return get(
      `${this.api}/mgo-api/api/v1/coa/${coaUUID}/check`, {}, {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      },
    );
  }

  /**
   * Check COA to see if there's exceptions
   * Used on ICOA flow - MoveCombined/Disclaimer, VerifyInfoEdit
   * @return {Promise}
   */
  async coaCheck() {
    return this.coaCheckByUUID(this.coaUUID);
  }

  /**
   * Verify COA using DOB
   * Used in DCOA flow
   * @param {String} birthDate - Formatted as yyyy-MM-dd
   * @return {Promise<String>}
   */
  async verifyAge(birthDate) {
    return post(`${this.api}/mgo-api/api/v1/identity/id-verification/${this.coaUUID}?birthdate=${birthDate}`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Verify Business DCOA
   * @return {Promise<String>}
   */
  async verifyBusinessDCOA(simulate = false) {
    return post(`${this.api}/mgo-api/api/v1/identity/id-verification/${this.coaUUID}${simulate ? '/simulate' : ''}`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }
  /**
   * COA Object Actions
   * ----------------------------------------------------------------
   */

  /**
    * Create the COA empty
    * This allows us to issue patches to the COA as information
    * becomes available
    * @param {String} utmMedium - utm_medium query param
    * @return {Promise<Boolean>}
    */
  async coaCreateNew(utmMedium = '') {
    const getChannel = () => {
      if (this.singleVal(utmMedium) === 'cpc') return 'paid';
      if (utmMedium) return this.singleVal(utmMedium);
      return 'organic';
    };

    return post(`${this.api}/mgo-api/api/v1/coa?deviceType=${this.deviceType}`, {
      origin: this.origin,
      sid: this.sid,
      channel: getChannel(),
      uuid: this.coaUUID, // uuid of the primary COA
      deviceType: this.deviceType,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }
  /**
   * COA Object Actions
   * ----------------------------------------------------------------
   */

  /**
    * Create the COA empty
    * This allows us to issue patches to the COA as information
    * becomes available
    * @param {String} utmMedium - utm_medium query param
    * @return {Promise<Boolean>}
    */
  async coaCreate(utmMedium = '') {
    if (this.coaUUID) {
      return Promise.resolve(true);
    }

    const { uuid, visitId } = await this.coaCreateNew(utmMedium);

    this.coaUUID = uuid || '';
    this.orderUuid = uuid || '';
    if (!this.visitId) this.visitId = visitId || '';

    if (!this.coaUUID) {
      console.log(new Error('unable to create coa'));
    }

    return Promise.resolve(!!uuid);
  }

  /**
   * Patch the COA as information is available
   * @param {Boolean} informedDelivery - User registered for ID
   * @param {String} moverType - INDIVIDUAL | FAMILY | PERMANENT
   * @param {String} forwardType - PERMANENT | TEMPORARY
   * @param {String} startDate - The start date of forwarding - yyyy-MM-dd
   * @param {String} stopDate - If the user is temporary, the end date of forwarding - yyyy-MM-dd
   * @param {String} billingAddressType - OLD | NEW | OTHER
   * @param {String} businessName - when forardType is BUSINESS
   * @param {String} email - email for reminders or confirmations
   * @return {Promise<Coa>}
   */
  async coaUpdate({
    /* eslint-disable */
    informedDelivery = false,
    moverType = null,
    forwardType = null,
    startDate = null,
    stopDate = null,
    billingAddressType = null,
    businessName = null,
    email = null,
    famPrompt1ShareLastName = null,
    famPrompt2SameLastNameRemaining = null,
    bizPromptEntireMoving = null,
    sdBuffered=false,
    earlyOptin = '0',
    multipleAddresses = false,
  }) {
    return put(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}`, {
      origin: this.origin,
      uuid: this.coaUUID,
      informedDelivery,
      moverType,
      forwardType,
      startDate,
      stopDate,
      billingAddressType,
      businessName,
      email,
      famPrompt1ShareLastName,
      famPrompt2SameLastNameRemaining,
      bizPromptEntireMoving,
      sdBuffered,
      earlyOptin: parseInt(earlyOptin),
      multipleAddresses,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

   /**
   * Patch the Business COA as information is available
   * @param {String} orderUuid - order UUID of business COA being updated
   * @param {Boolean} informedDelivery - User registered for ID
   * @param {String} moverType - INDIVIDUAL | FAMILY | PERMANENT
   * @param {String} forwardType - PERMANENT | TEMPORARY
   * @param {String} startDate - The start date of forwarding - yyyy-MM-dd
   * @param {String} stopDate - If the user is temporary, the end date of forwarding - yyyy-MM-dd
   * @param {String} billingAddressType - OLD | NEW | OTHER
   * @param {String} businessName - when forardType is BUSINESS
   * @param {String} email - email for reminders or confirmations
   * @return {Promise<Coa>}
   */
   async businessCoaUpdate({
    /* eslint-disable */
    orderUuid = '',
    multipleAddresses = false,
    informedDelivery = false,
    moverType = null,
    forwardType = null,
    startDate = null,
    stopDate = null,
    billingAddressType = null,
    businessName = null,
    email = null,
    famPrompt1ShareLastName = null,
    famPrompt2SameLastNameRemaining = null,
    bizPromptEntireMoving = null,
    sdBuffered=false,
    earlyOptin = '0',
  }) {
    const targetOrderUuid = orderUuid || this.orderUuid || this.coaUUID;

    return put(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/business/${targetOrderUuid}`, {
      origin: this.origin,
      multipleAddresses,
      uuid: this.coaUUID,
      informedDelivery,
      moverType,
      forwardType,
      startDate,
      stopDate,
      billingAddressType,
      businessName,
      email,
      famPrompt1ShareLastName,
      famPrompt2SameLastNameRemaining,
      bizPromptEntireMoving,
      sdBuffered,
      earlyOptin: parseInt(earlyOptin),
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Delete a business COA for multiple businesses
   * @param {String} orderUuid - order UUID of business COA being updated
   * @return {Promise<Coa>}
   */
  async businessDeleteCoa(orderUuid) {
    return await del(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/business/${orderUuid}`, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      }
    })
  }


  /**
   * Patch the COA as information is available - Preliminary COA update call for simplify
   * @param {String} moverType - INDIVIDUAL | FAMILY | PERMANENT
   * @return {Promise<Coa>}
   */
  async coaUpdateV2({
    /* eslint-disable */
    moverType = null,
    famPrompt1ShareLastName = null,
    famPrompt2SameLastNameRemaining = null,
    bizPromptEntireMoving = null,
    multipleAddresses = false
  }) {
    return put(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/partial`, {
      origin: this.origin,
      uuid: this.coaUUID,
      moverType,
      famPrompt1ShareLastName,
      famPrompt2SameLastNameRemaining,
      bizPromptEntireMoving,
      multipleAddresses,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }
     /**
   * Patch the Business COA as information is available
   * @param {String} orderUuid - order UUID of business COA being updated
   * @param {String} moverType - INDIVIDUAL | FAMILY | BUSINESS
   * @param {String} businessName - when forardType is BUSINESS
   * @param {String} multipleAddresses - true for multiple business COA
   * @return {Promise<Coa>}
   */
     async businessPartialCoaUpdate({
      /* eslint-disable */
      orderUuid = '',
      multipleAddresses = false,
      moverType = null,
      businessName = null,
      famPrompt1ShareLastName = null,
      famPrompt2SameLastNameRemaining = null,
      bizPromptEntireMoving = null,
    }) {
      const targetOrderUuid = orderUuid || this.orderUuid || this.coaUUID;

      return put(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/business/${targetOrderUuid}/partial`, {
        origin: this.origin,
        multipleAddresses,
        uuid: this.coaUUID,
        moverType,
        businessName,
        famPrompt1ShareLastName,
        famPrompt2SameLastNameRemaining,
        bizPromptEntireMoving,
      }, {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      });
    }
  /**
   * Retrieve the COA data
   * Used for rehydrating the user experience
   * and rendering next page information
   * @return {Promise<{visitId, Coa, moverType: string, forwardType:string}>}
   */
  async coaGet() {
    if (!this.coaUUID) return {};

    const { visitId, ...coa } = await get(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });

    this.visitId = visitId;

    // Set IVS session variables from backend response
    if (coa.ivsProofingCompleted !== undefined) {
      sessionStorage.setItem('ivsProofingCompleted', JSON.stringify(coa.ivsProofingCompleted));
    }
    if (coa.ivsVerificationPassed !== undefined) {
      sessionStorage.setItem('ivsVerificationPassed', JSON.stringify(coa.ivsVerificationPassed));
    }
    if (coa.ivsProfilingCompleted !== undefined) {
      sessionStorage.setItem('ivsProfilingCompleted', JSON.stringify(coa.ivsProfilingCompleted));
    }

    return { visitId, ...coa, moverType: coa.moverType || '',forwardType: coa.forwardType || '' };
  }

  /**
   * Used to finalize a COA
   * after it has been verified
   * @param {String} orderNumber - Order Number used in GP
   * @param {String} cuid - Used in DCOA Verification
   * @return {Promise<String>}
   */
  async coaCommit({
    coaUUID = '',
    orderNumber = null,
    cuid = null,
    hasMultipleBusiness = false
  }) {
    let url = `${this.api}/mgo-api/api/v2/coa/${coaUUID}/commit`;

    if (hasMultipleBusiness) {
      url = `${this.api}/mgo-api/api/v2/coa/${coaUUID}/commit/business`;
    }
    const item = url.split('?');
    const outUrl = url?.replace(`?${item[1]}`, `/commit?${item[1]?.replace('/commit', '')}`);

    const { cfpsId } = await post(`${outUrl}`, {
      orderNumber,
      cuid,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });

    return cfpsId;
  }

  /**
   * Mark a Military COA, confirmed
   * @property {COA} coa - The coa data from the flow
   * @return {Promize}
   */
  async confirmMCOA(coa) {
    return post(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/mcoa-confirm`, coa || {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      }
    });
  }

  /**
   * Exchange a single-use military email invitation for this browser session's
   * narrowly scoped CONFIRM grant.
   * @param {String} invitationToken - raw invitation read from the URL fragment
   * @return {Promise<MilitaryConfirmation>}
   */
  async militaryConfirmationExchange(invitationToken) {
    return post(`${this.api}/mgo-api/api/v1/military-confirmation/exchange`, {
      token: invitationToken,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Reload the minimal military confirmation view after the invitation has
   * already been exchanged by this browser session.
   * @param {String} orderUuid - granted military COA identifier
   * @return {Promise<MilitaryConfirmation>}
   */
  async militaryConfirmationGet(orderUuid = this.coaUUID) {
    return get(`${this.api}/mgo-api/api/v1/military-confirmation/${orderUuid}`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Reject the pending military COA using the current session's CONFIRM grant.
   * @param {String} orderUuid - granted military COA identifier
   * @return {Promise}
   */
  async militaryConfirmationReject(orderUuid = this.coaUUID) {
    return post(`${this.api}/mgo-api/api/v1/military-confirmation/${orderUuid}/reject`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Handle COA ID signup on email return
   * @return {Promise}
   */
  async coaInformedDeliverySignup() {

    return post(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/infdel`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      }
    });
  }

  /**
   * Address Object Actions
   * ----------------------------------------------------------------
   */

  /**
    * Save an Address Object to a COA
    * @param {String} addressType - OLD | NEW | BILLING
    * @param {String} streetAddress
    * @param {String} city
    * @param {String} state
    * @param {String} zipCode
    * @param {String} urbanizationName - Only Used for Puerto Rico
    * @param {String} poBoxNumber
    * @param {String} privateMailbox
    * @param {Boolean} overrideAms
    * @param {Boolean} recentConstruction
    * @param {String} moverType
    * @param {String} forwardType
    * @return {Promise<Address>}
    */

   async addressSaveV2({
    streetAddress: oldStreetAddress = '',
    city: oldCity = '',
    state: oldState = '',
    zipCode: oldZipCode = '',
    urbanizationName: oldUrbanizationName = null,
    poBoxNumber: oldPoBoxNumber = null,
    privateMailbox : oldPrivateMailbox = null,
    overrideAms : oldOverrideAms= false,
    recentConstruction : oldRecentConstruction = false,
    moverType: oldMoverType = '',
    forwardType : oldForwardType = '',
  }, {
    streetAddress: newStreetAddress = '',
    city: newCity = '',
    state: newState = '',
    zipCode: newZipCode = '',
    urbanizationName: newUrbanizationName = null,
    poBoxNumber: newPoBoxNumber = null,
    privateMailbox : newPrivateMailbox = null,
    overrideAms : newOverrideAms= false,
    recentConstruction : newRecentConstruction = false,
    moverType: newMoverType = '',
    forwardType : newForwardType = '',
  }) {
    sessionStorage.setItem('coaUUID', this.coaUUID);
    return post(`${this.api}/mgo-api/api/v2/coa/${this.coaUUID}/addresses`, {
      oldAddress: {
        orderUuid: this.coaUUID,
        addressType: "OLD",
        streetAddress: oldStreetAddress,
        city: oldCity,
        state: oldState,
        zipCode: oldZipCode,
        urbanizationName: oldUrbanizationName,
        poBoxNumber: oldPoBoxNumber,
        privateMailbox : oldPrivateMailbox,
        overrideAms : oldOverrideAms,
        recentConstruction : oldRecentConstruction,
        moverType: oldMoverType,
        forwardType : oldForwardType,
      },
      newAddress: {
        orderUuid: this.coaUUID,
        addressType: "NEW",
        streetAddress: newStreetAddress,
        city: newCity,
        state: newState,
        zipCode: newZipCode,
        urbanizationName: newUrbanizationName,
        poBoxNumber: newPoBoxNumber,
        privateMailbox : newPrivateMailbox,
        overrideAms : newOverrideAms,
        recentConstruction : newRecentConstruction,
        moverType: newMoverType,
        forwardType : newForwardType,
      }
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Address Object Actions Simplify
   * ----------------------------------------------------------------
   */

  /**
    * Save an Address Object to a COA
    * @param {String} addressType - OLD | NEW | BILLING
    * @param {String} streetAddress
    * @param {String} city
    * @param {String} state
    * @param {String} zipCode
    * @param {String} urbanizationName - Only Used for Puerto Rico
    * @param {String} poBoxNumber
    * @param {String} privateMailbox
    * @param {Boolean} overrideAms
    * @param {Boolean} recentConstruction
    * @param {String} moverType
    * @return {Promise<Address>}
    */

  async addressSaveV2Simplify({
    streetAddress: oldStreetAddress = '',
    city: oldCity = '',
    state: oldState = '',
    zipCode: oldZipCode = '',
    urbanizationName: oldUrbanizationName = null,
    poBoxNumber: oldPoBoxNumber = null,
    privateMailbox : oldPrivateMailbox = null,
    overrideAms : oldOverrideAms= false,
    recentConstruction : oldRecentConstruction = false,
    moverType: oldMoverType = '',
  }, {
    streetAddress: newStreetAddress = '',
    city: newCity = '',
    state: newState = '',
    zipCode: newZipCode = '',
    urbanizationName: newUrbanizationName = null,
    poBoxNumber: newPoBoxNumber = null,
    privateMailbox : newPrivateMailbox = null,
    overrideAms : newOverrideAms= false,
    recentConstruction : newRecentConstruction = false,
    moverType: newMoverType = '',
  }) {
    sessionStorage.setItem('coaUUID', this.coaUUID);
    return post(`${this.api}/mgo-api/api/v2/coa/${this.coaUUID}/addresses`, {
      oldAddress: {
        orderUuid: this.coaUUID,
        addressType: "OLD",
        streetAddress: oldStreetAddress,
        city: oldCity,
        state: oldState,
        zipCode: oldZipCode,
        urbanizationName: oldUrbanizationName,
        poBoxNumber: oldPoBoxNumber,
        privateMailbox : oldPrivateMailbox,
        overrideAms : oldOverrideAms,
        recentConstruction : oldRecentConstruction,
        moverType: oldMoverType,
      },
      newAddress: {
        orderUuid: this.coaUUID,
        addressType: "NEW",
        streetAddress: newStreetAddress,
        city: newCity,
        state: newState,
        zipCode: newZipCode,
        urbanizationName: newUrbanizationName,
        poBoxNumber: newPoBoxNumber,
        privateMailbox : newPrivateMailbox,
        overrideAms : newOverrideAms,
        recentConstruction : newRecentConstruction,
        moverType: newMoverType,
      }
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Business Address Object Actions
   * ----------------------------------------------------------------
   */

  /**
    * Save an Address Object to a COA
    * @param {String} orderUuid
    * @param {String} addressType - OLD | NEW | BILLING
    * @param {String} streetAddress
    * @param {String} city
    * @param {String} state
    * @param {String} zipCode
    * @param {String} urbanizationName - Only Used for Puerto Rico
    * @param {String} poBoxNumber
    * @param {String} privateMailbox
    * @param {Boolean} overrideAms
    * @param {Boolean} recentConstruction
    * @param {String} moverType
    * @param {String} forwardType
    * @return {Promise<Address>}
    */

   async businessAddressSave(orderUuid,{
    streetAddress: oldStreetAddress = '',
    city: oldCity = '',
    state: oldState = '',
    zipCode: oldZipCode = '',
    urbanizationName: oldUrbanizationName = null,
    poBoxNumber: oldPoBoxNumber = null,
    privateMailbox : oldPrivateMailbox = null,
    overrideAms : oldOverrideAms= false,
    recentConstruction : oldRecentConstruction = false,
    moverType: oldMoverType = '',
    forwardType: oldForwardType = '',
  }, {
    streetAddress: newStreetAddress = '',
    city: newCity = '',
    state: newState = '',
    zipCode: newZipCode = '',
    urbanizationName: newUrbanizationName = null,
    poBoxNumber: newPoBoxNumber = null,
    privateMailbox : newPrivateMailbox = null,
    overrideAms : newOverrideAms= false,
    recentConstruction : newRecentConstruction = false,
    moverType: newMoverType = '',
    forwardType: newForwardType = ''
  }) {
    sessionStorage.setItem('coaUUID', this.coaUUID);
    return post(`${this.api}/mgo-api/api/v2/coa/${this.coaUUID}/busaddr/${orderUuid}`, {
      oldAddress: {
        orderUuid: this.coaUUID,
        addressType: "OLD",
        streetAddress: oldStreetAddress,
        city: oldCity,
        state: oldState,
        zipCode: oldZipCode,
        urbanizationName: oldUrbanizationName,
        poBoxNumber: oldPoBoxNumber,
        privateMailbox : oldPrivateMailbox,
        overrideAms : oldOverrideAms,
        recentConstruction : oldRecentConstruction,
        moverType: oldMoverType,
        forwardType: oldForwardType,
      },
      newAddress: {
        orderUuid: this.coaUUID,
        addressType: "NEW",
        streetAddress: newStreetAddress,
        city: newCity,
        state: newState,
        zipCode: newZipCode,
        urbanizationName: newUrbanizationName,
        poBoxNumber: newPoBoxNumber,
        privateMailbox : newPrivateMailbox,
        overrideAms : newOverrideAms,
        recentConstruction : newRecentConstruction,
        moverType: newMoverType,
        forwardType: newForwardType,
      }
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  async businessAddressSaveSimplify(orderUuid,{
    streetAddress: oldStreetAddress = '',
    city: oldCity = '',
    state: oldState = '',
    zipCode: oldZipCode = '',
    urbanizationName: oldUrbanizationName = null,
    poBoxNumber: oldPoBoxNumber = null,
    privateMailbox : oldPrivateMailbox = null,
    overrideAms : oldOverrideAms= false,
    recentConstruction : oldRecentConstruction = false,
    moverType: oldMoverType = '',
  }, {
    streetAddress: newStreetAddress = '',
    city: newCity = '',
    state: newState = '',
    zipCode: newZipCode = '',
    urbanizationName: newUrbanizationName = null,
    poBoxNumber: newPoBoxNumber = null,
    privateMailbox : newPrivateMailbox = null,
    overrideAms : newOverrideAms= false,
    recentConstruction : newRecentConstruction = false,
    moverType: newMoverType = '',
  }) {
    sessionStorage.setItem('coaUUID', this.coaUUID);
    return post(`${this.api}/mgo-api/api/v2/coa/${this.coaUUID}/busaddr/${orderUuid}`, {
      oldAddress: {
        orderUuid: this.coaUUID,
        addressType: "OLD",
        streetAddress: oldStreetAddress,
        city: oldCity,
        state: oldState,
        zipCode: oldZipCode,
        urbanizationName: oldUrbanizationName,
        poBoxNumber: oldPoBoxNumber,
        privateMailbox : oldPrivateMailbox,
        overrideAms : oldOverrideAms,
        recentConstruction : oldRecentConstruction,
        moverType: oldMoverType,
      },
      newAddress: {
        orderUuid: this.coaUUID,
        addressType: "NEW",
        streetAddress: newStreetAddress,
        city: newCity,
        state: newState,
        zipCode: newZipCode,
        urbanizationName: newUrbanizationName,
        poBoxNumber: newPoBoxNumber,
        privateMailbox : newPrivateMailbox,
        overrideAms : newOverrideAms,
        recentConstruction : newRecentConstruction,
        moverType: newMoverType,
      }
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  async businessBillingAddressSave(orderUuid,{
    streetAddress: billingStreetAddress = '',
    city: billingCity = '',
    state: billingState = '',
    zipCode: billingZipCode = '',
    urbanizationName: billingUrbanizationName = null,
    poBoxNumber: billingPoBoxNumber = null,
    privateMailbox: billingPrivateMailbox = null,
    overrideAms: billingOverrideAms = false,
    recentConstruction: billingRecentConstruction = false,
  }) {
    return post(`${this.api}/mgo-api/api/v2/coa/${this.coaUUID}/busaddr/${orderUuid}`, {
      billingAddress: {
        orderUuid: this.coaUUID,
        addressType: "BILLING",
        streetAddress: billingStreetAddress,
        city: billingCity,
        state: billingState,
        zipCode: billingZipCode,
        urbanizationName: billingUrbanizationName,
        poBoxNumber: billingPoBoxNumber,
        privateMailbox: billingPrivateMailbox,
        overrideAms: billingOverrideAms,
        recentConstruction: billingRecentConstruction,
      },
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  async businessBillingAddressDelete() {
    return del(`${this.api}/mgo-api/api/v2/coa/${this.coaUUID}/busaddr`, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  async addressSave({
    addressType = 'OLD',
    streetAddress = '',
    city = '',
    state = '',
    zipCode = '',
    urbanizationName = null,
    poBoxNumber = null,
    privateMailbox = null,
    overrideAms = false,
    recentConstruction = false,
  }) {
    return post(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/addresses`, {
      orderUuid: this.coaUUID,
      addressType,
      streetAddress,
      city,
      state,
      zipCode,
      urbanizationName,
      poBoxNumber,
      privateMailbox,
      overrideAms,
      recentConstruction,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  async addressDelete(addressUUID) {
    return await del(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/addresses/${addressUUID}`, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      }
    })
  }


  /**
   * Retreive all Address associated to a COA
   * @return {Promise<Address[]>}
   */
  async addressGetAll() {
    const response = await get(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/addresses`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });

    if (Array.isArray(response)) {
      return { addresses: response };
    }

    return { ...response, addresses: [] };
  }

    /**
   * Retreive all Business Addresses associated to a COA
   * @return {Promise<Address[]>}
   */
    async busAddrGetAll() {

      const response = await get(`${this.api}/mgo-api/api/v2/coa/${this.coaUUID}/busaddr`, {}, {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      });

      if (Array.isArray(response)) {
        return { addresses: response };
      }

      return { ...response, addresses: [] };
    }

  /**
   * Retreive all Address associated to a COA
   * @return {Promise<{thirdAddressEligible: boolean, oldAddress: Address, newAddress: Address, thirdAddress: Address}>}
   */
  async addressGetAllV2() {
    return get(`${this.api}/mgo-api/api/v2/coa/${this.coaUUID}/addresses`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * This is used to log exceptions created by invalid address data
   * @param {String} exception - The API response exception
   * @param {Object} oldAddress - basic old address properties
   * @param {Object} newAddress - basic new address properties
   * @return {Promise}
   */
  async addressException(exception, {
    streetAddress: oldStreetAddress = null,
    city: oldCity = null,
    state: oldState = null,
    zipCode: oldZipCode = null,
  }, {
    streetAddress: newStreetAddress = null,
    city: newCity = null,
    state: newState = null,
    zipCode: newZipCode = null,
  }, {
    streetAddress: thirdStreetAddress = null,
    city: thirdCity = null,
    state: thirdState = null,
    zipCode: thirdZipCode = null,
  }) {
    if (exception !== "EXACT_MATCH") {
      return post(`${this.api}/mgo-api/api/v1/exception/${this.coaUUID}`, {
        exception,
        oldAddress: {
          streetAddress: oldStreetAddress,
          city: oldCity,
          state: oldState,
          zipCode: oldZipCode,
        },
        newAddress: {
          streetAddress: newStreetAddress,
          city: newCity,
          state: newState,
          zipCode: newZipCode,
        },
        thirdAddress: {
          streetAddress: thirdStreetAddress,
          city: thirdCity,
          state: thirdState,
          zipCode: thirdZipCode,
        },
      }, {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      });
      }
      return post(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/log-validation`, {}, {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });
  }

  /**
   * Person Object Actions
   * ----------------------------------------------------------------
   */

  /**
    * Saves a Person object to a COA
    * @param {String} personType - MOVER | PREPARER
    * @param {String} firstName
    * @param {String} middleName
    * @param {String} lastName
    * @param {String} email
    * @param {String} mobilePhone - Required if homePhone not present
    * @param {String} homePhone - Required if mobilePhone not present
    * @return {Promise<Person>}
    */
  async personSave({
    personType = 'MOVER',
    firstName = '',
    middleName = '',
    lastName = '',
    email = '',
    ...params
  }) {
    return post(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/persons`, {
      orderUuid: this.coaUUID,
      personType,
      firstName,
      middleName,
      lastName,
      email,
      emailConfirm: email,
      ...params, // for phone field
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Person Object Actions for multiple businesses
   * ----------------------------------------------------------------
   */

  /**
    * Saves a Person object to a COA - Updates person records for all secondary and primary
   * business
    * @param {String} personType - MOVER | PREPARER
    * @param {String} firstName
    * @param {String} middleName
    * @param {String} lastName
    * @param {String} email
    * @param {String} mobilePhone - Required if homePhone not present
    * @param {String} homePhone - Required if mobilePhone not present
    * @return {Promise<Person>}
    */
  async businessPersonSave({
    personType = 'MOVER',
    firstName = '',
    middleName = '',
    lastName = '',
    email = '',
    ...params
  }) {
    return put(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/business/persons`, {
      orderUuid: this.coaUUID,
      personType,
      firstName,
      middleName,
      lastName,
      email,
      emailConfirm: email,
      ...params, // for phone field
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }
  /**
   * Person Object Actions
   * ----------------------------------------------------------------
   */

  /**
    * Saves a Person of a specific orderUuid object to a COA
    * @param {String} orderUuid
    * @param {String} personType - MOVER | PREPARER
    * @param {String} firstName
    * @param {String} middleName
    * @param {String} lastName
    * @param {String} email
    * @param {String} mobilePhone - Required if homePhone not present
    * @param {String} homePhone - Required if mobilePhone not present
    * @return {Promise<Person>}
    */
  async personSaveByOrderUuid(orderUuid,{
    personType = 'MOVER',
    firstName = '',
    middleName = '',
    lastName = '',
    email = '',
    ...params
  }) {
    return post(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/persons/${orderUuid}`, {
      orderUuid: this.coaUUID,
      personType,
      firstName,
      middleName,
      lastName,
      email,
      emailConfirm: email,
      ...params, // for phone field
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }
    /**
   * Retreive all Person Objects on a COA
   * @return {Promise<Person[]>}
   */
  async personGetAll() {
    return this.personGetAllByUuid(this.coaUUID);
  }
  /**
   * Retreive all Person Objects on a COA
   * @return {Promise<Person[]>}
   */
  async personGetAllByUuid(coaUUID) {
    return await get(`${this.api}/mgo-api/api/v2/coa/${coaUUID}/persons`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

    /**
   * Retreive all Person Objects on a COA
   * @return {Promise<Person[]>}
   */
    async businessGetAll() {

      return await get(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/business`, {}, {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      });
    }

  async personDelete() {

    return await del(`${this.api}/mgo-api/api/v2/coa/${this.coaUUID}/persons`, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },

    })
  }

  async businessPersonDelete() {

    return await del(`${this.api}/mgo-api/api/v2/coa/${this.coaUUID}/business/persons`, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    })
  }

    /**
    * Determine if COA qualifies for Extended Mail Forwarding (EMF)
    * @param {String} cfpsid - required if orderUuid not present
    * @param {String} orderUuid - required if cfpsid not present
    * @param {String} forwardType - PERMANENT | TEMPORARY
    * @param {Boolean} dcoa - is Distaster COA
    * @param {Boolean} mcoa - is Military COA
    * @param {String} oldZip5
    * @param {String} newZip5
    * @return {Promise<Prescreen>}
    */
     async extensionPrescreen({
      cfpsid = '',
      orderUuid = '',
      forwardType = '',
      dcoa = false,
      mcoa = false,
      oldZip5 = '',
      newZip5 = '',
    }) {
      if (forwardType!== 'PERMANENT') return { eligible: false };
      const extensionList = await this.extensionGetAll(this.coa?.visitId);
      return { eligible: (extensionList?.availableExtensions?.length > 0) ? true : false };
    }

  async personGet(type = 'mover') {
    return get(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/mover`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      }
    });
  }

  /**
   * EMF Actions
   * ----------------------------------------------------------------
   */

  /**
   * Get EMF option data
   * @return {Promise<EMFResponse>}
   */
  async extensionGetAll(visitId) {
    return get(`${this.api}/mgo-api/api/v1/extension/${this.coaUUID}`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * persist users emf selection
   * @param {String} optionId
   * @param {String} visitId
   * @return {Promise}
   */
  async extensionSave(optionId, visitId) {
    return post(`${this.api}/mgo-api/api/v1/extension/${this.coaUUID}?option=${optionId}`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Business Alias Actions
   * ----------------------------------------------------------------
   */

  /**
    * Save Business Alias Data
    * Can be an empty list
    * @param {String[]} aliases - a list of strings representing each alias
    * @return {Promise<AliasResponse>}
    */
  async businessAliasSave([...aliases]) {
    return post(`${this.api}/mgo-api/api/v1/alias/${this.coaUUID}`, {
      aliases,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }
  /**
   * Save Business Alias Data
   * Can be an empty list
   * @param {String} orderUuid - order UUID uniquely identifies this alias list in a multiple business COA
   * @param {String[]} aliases - a list of strings representing each alias
   * @return {Promise<AliasResponse>}
   */
 async businessAliasSaveByOrderUuid(orderUuid, [...aliases]) {

   return post(`${this.api}/mgo-api/api/v1/alias/${this.coaUUID}/orders/${orderUuid}`, {
     aliases,
   }, {
     headers: {
       Authorization: `Bearer ${this.token}`,
     },
   });
 }

  /**
   * Get all business alias data on a COA
   * @return {AliasResponse}
   */
  async businessAliasGetAll() {
    return get(`${this.api}/mgo-api/api/v1/alias/${this.coaUUID}`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

    /**
   * Get all business alias by orderUuid data on a COA
   * @return {AliasResponse}
   */
    async businessAliasGetAllByOrderUuid(orderUuid) {

      return get(`${this.api}/mgo-api/api/v1/alias/${this.coaUUID}/orders/${orderUuid}`, {}, {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      });
    }

  /**
   * Email Actions
   * ----------------------------------------------------------------
   */

  /**
   * Allow the COA to opt out of email reminders
   * @return {Promise}
   */
  async emailReminderOptOut() {
    return post(`${this.api}/mgo-api/api/v1/e/unsubscribe/${this.coaUUID}?type=reminder`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }
  async emailReminderOptIn() {
    return post(`${this.api}/mgo-api/api/v1/event/subscribe/${this.coaUUID}?type=reminder`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Send post-commit emails
   * @param {Boolean} optedIntoDeals - whether the mover opted in to the instant-savings deals email
   */
  async emailSend(optedIntoDeals) {
    if (this.coaUUID.length>0) {

      const [coaUUID, clientID] = this.coaUUID.split('?');
      this.clientID = clientID
      if(JSON.parse(sessionStorage.getItem('optIntoIPP'))) {
        // IPP confirmation is intentionally withheld here - the backend order-status
        // check blocks it until proofing is resolved - so only deals can go out, and
        // only when opted in.
        if (!optedIntoDeals) return undefined;
        return post(`${this.api}/mgo-api/api/v1/email/${coaUUID}/deals/send`, {}, {
          headers: {
            Authorization: `Bearer ${this.token}`,
          }
        });
      } else if (optedIntoDeals) {
        return post(`${this.api}/mgo-api/api/v1/email/${coaUUID}/send`, {}, {
          headers: {
            Authorization: `Bearer ${this.token}`,
          }
        });
     } else {
        return post(`${this.api}/mgo-api/api/v1/email/${coaUUID}/confirmation/send`, {}, {
          headers: {
            Authorization: `Bearer ${this.token}`,
          }
        });
     }
    }
    return undefined;
  }

  /**
   * Unsubscribe email
   * @param {String} coaUUID - COA order uuid
   * @param {String} ref - Email template ref
   * @param {String} jwt - token provided by unsub link query params
   * @return {Promise<String>}
   */
  async unsubscribe(coaUUID, ref, jwt = '') {
    return post(`${this.api}/mgo-api/api/v1/email/unsubscribe`, {
      jwt,
      ref,
      uuid: coaUUID,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * MYMOVE
   * ----------------------------------------------------------------
   */
  /**
   * Get Access Handoff URL
   * @param {String} dest - Access flow to send the user down (ex. 'vr' or 'offers')
   * @param {Boolean} autofill - User opted in to allow data transfer to MYMOVE
   * @param {Object} queryParamObj - Query params key value pairs used to override utm params
   * @return {Promise}
   */
  async handoffGetURL(dest, autofill,  {utm_campaign = 'default', utm_medium = 'sp',utm_source = 'usps'}) {
    const coaUUID = window.location.pathname.split('/').pop();
    return get(`${this.api}/mgo-api/api/v1/handoffs/${coaUUID}`, {
      params: {
        ...getAmpersendParams(),
        ...getAllAmpersendParams(),
        destinationUrl: getDestinationURLParam(),
        autofill,
        dest,
        campaign: utm_campaign,
        medium: utm_medium,
        source: utm_source,
      },
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Analytics Events
   * ----------------------------------------------------------------
   */

  /**
   * Get the visitId
   * @param {String} deviceType - DESKTOP | MOBILE
   * @return {Promise<String>}
   */
  async visit(deviceType) {
    const visitId = await post(
      `${this.api}/mgo-api/api/v1/e/v`,
      {
        params: {
          qs: location.search.slice(1),
          deviceType,
        },
      },
      {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      },
    );

    this.visitId = visitId || '';
  }
  /**
   * Return first value if input is an array
   * @param {*} val
   * @returns
   */
 singleVal(val){
    return val && Array.isArray(val) && val.length ? val[0] : val;
  }
  /**
    * Track Segment server page view event
    * @param {String} deviceType - DESKTOP | MOBILE
    * @param {String} pageId - ID from analytics route map
    * @param {String} pageName - route name
    * @param {String} adgid - utm param
    * @param {String} gclid - utm param
    * @param {String} source - utm param
    * @return {Promise<String>}
    */
  async pageView({
    deviceType,
    pageId,
    pageName,
    adgid,
    gclid,
    source,
    referral,
  }) {
    // context of this is missing a token after visit is called
    const token = this.token;
    if (!this.visitId) {
      await this.visit(deviceType);
    }

    if ((this.coaUUID || this.visitId) && pageId) {
      const [coaUUID, clientID] = this.coaUUID.split('?')
      this.clientID = clientID;
      const payload = {
        sid: this.sid,
        [coaUUID ? 'uuid' : 'visitId']: coaUUID || this.visitId,
        deviceType,
        pageName,
        referral: this.singleVal(referral),
        context_campaign_adgid: this.singleVal(adgid),
        context_campaign_gclid: this.singleVal(gclid),
        context_campaign_source: this.singleVal(source),
      };

      if (pageId.toString().indexOf(',') >= 0) {
        payload.viewCode = pageId.toString();
      } else {
        payload.pageId = pageId;
      }

      return post(
        `${this.api}/mgo-api/api/v1/e/pv`,
        { ...payload },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    }

    return Promise.resolve(!!pageId)
  }

  async eventTrack(event, properties = {}) {
    try {
      if (this.coaUUID) {
        await post(`${this.api}/mgo-api/api/v1/e/${this.coaUUID}`,
          { event, properties, },
          {
            headers: {
              Authorization: `Bearer ${this.token}`,
            },
          },
        );
      } else {
        setTimeout(()=> {this.eventTrack(event, properties)}, 1000);
      }
    } catch (error) {
      this.$store.dispatch('setDevNull', error);
    }

    return Promise.resolve(true);
  }

  /**
   * Track engagement event
   * @param {String} refId - event reference id
   * @param {Array} offerIds - ids of offers to track
   * @return {Promise}
   */
  async trackEngagement(refId, offerIds) {
    const events = {
      110: {
        events: 'event110',
        name: 'See Details Page - Dedicated Email',
        products: offerIds,
        refId,
        properties: { evar6: '' },
      },
      111: {
        events: 'event111',
        name: 'Redeem Offer - Dedicated Email',
        products: offerIds,
        refId,
        properties: { evar6: '' },
      },
      112: {
        events: 'event112',
        name: 'SMS Opened',
        products: offerIds,
        refId: 'sms',
        properties: { evar6: '' },
      },
      114: {
        events: 'event114',
        name: 'Client Logo Click',
        products: offerIds,
        refId,
        properties: { evar6: '' },
      },
    };

    const refIds = {
      '10031hdr-lbl': events['110'],
      '10033hdr-lbl': events['110'],
      '10031redeem': events['111'],
      '10033redeem': events['111'],
      sms: events['112'],
      '10031logo': events['114'],
      '10033logo': events['114'],
    };

    const payload = refIds[refId] || {};

    if (payload && payload.events) {

     return post(
        `${this.api}/mgo-api/api/v1/e/${this.coaUUID ?? parsedUUID}`,
        { ...payload },
        {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        },
      );
    }

    return Promise.resolve(payload && payload.events)
  }

  /**
   * Track continuation to confirmation page
   * @param {('EMAIL'|'PRINT'|'SMS')} type - event type
   * @param {Array} offerIds - ids of offers to track
   * @param {Boolean} getAll - all offers selected
   * @return {Promise}
   */
  async trackContinueToConfirm(type = 'EMAIL', offerIds, getAll = false) {
    const coaUUIDUrl = new URL(window.location)?.pathname;
    const parsedUUID = coaUUIDUrl?.split('/')?.pop();

    const response = await post(
      `${this.api}/mgo-api/api/v1/e/${this.coaUUID? this.coaUUID : parsedUUID}`,
      {
        event: 'event105',
        properties: {
          evar1: 'ICOA',
          evar6: '',
          prop2: '',
          evar4: type,
          evar25: getAll ? 'GET_ALL' : 'GET_SELECTED',
        },
      },
      {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      },
    );
    sessionStorage.removeItem('coaUUID');
    return response;
  }
}
